import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useLocations } from "../../../stores/User/user.queries";
import { useLocationId } from "../../../stores/User/User.hooks";
import { useLocationAbsences } from "../../../stores/Calendar/calendar.queries";
import { getAbsenceTypeLabel, normalizeAbsenceType } from "../Users/Calendar/absenceTypes";
import { summarizeAbsences } from "./absenceSummary";
import FlatCard from "../../../atoms/FlatCard";
import { USER_SUBTABS } from "../Users/UserRightPanel";
import type { LocationLeaveOfAbsenceDto } from "../../../api-client";

const formatDay = (value?: string) => (value ? moment(value).format("DD.MM.YYYY") : "");

const DATE_FORMAT = "YYYY-MM-DD";

/**
 * Overview of every leave of absence of the employees of one location.
 * Selecting a row opens that employee's absence calendar on the month of the entry.
 */
const LocationAbsences: React.FC = () => {
  const navigate = useNavigate();
  const currentLocationId = useLocationId();
  const { data: locations = [] } = useLocations();
  const [selectedLocation, setSelectedLocation] = useState<string>("");

  const locationId = selectedLocation || currentLocationId || "";
  const { data: absences = [], isLoading } = useLocationAbsences(locationId);

  const [userFilter, setUserFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const userOptions = useMemo(() => {
    const byId = new Map<string, string>();
    absences.forEach((absence) => byId.set(absence.userId, absence.userName));
    return [...byId.entries()].sort((a, b) => a[1].localeCompare(b[1], "de"));
  }, [absences]);

  const typeOptions = useMemo(
    () =>
      [...new Set(absences.map((absence) => normalizeAbsenceType(absence.loAType)).filter(Boolean))].sort((a, b) =>
        getAbsenceTypeLabel(a).localeCompare(getAbsenceTypeLabel(b), "de")
      ),
    [absences]
  );

  const visibleAbsences = useMemo(
    () =>
      absences
        .filter((absence) => {
          if (userFilter && absence.userId !== userFilter) return false;
          if (typeFilter && normalizeAbsenceType(absence.loAType) !== typeFilter) return false;
          // The stored end is exclusive, so the last absent day is one day earlier.
          const lastDay = moment(absence.end).subtract(1, "day").format(DATE_FORMAT);
          const firstDay = moment(absence.start).format(DATE_FORMAT);
          if (fromDate && lastDay < fromDate) return false;
          if (toDate && firstDay > toDate) return false;
          return true;
        })
        .sort((a, b) => (a.start || "").localeCompare(b.start || "")),
    [absences, userFilter, typeFilter, fromDate, toDate]
  );

  const summary = useMemo(
    () =>
      summarizeAbsences(
        visibleAbsences.map((absence) => ({
          loAType: absence.loAType,
          start: absence.start,
          // The stored end is exclusive; the summary counts the last absent day.
          end: moment(absence.end).subtract(1, "day").format(DATE_FORMAT),
        })),
        { from: fromDate || undefined, to: toDate || undefined }
      ),
    [visibleAbsences, fromDate, toDate]
  );

  const openAbsence = (absence: LocationLeaveOfAbsenceDto) => {    const params = new URLSearchParams({
      tabSelection: "user",
      userId: absence.userId,
      userSubTab: USER_SUBTABS.VACATION,
      absenceDate: moment(absence.start).format("YYYY-MM-DD"),
    });
    navigate(`/admin?${params.toString()}`);
  };

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2, pr: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="absences-location-label">Standort</InputLabel>
          <Select
            labelId="absences-location-label"
            label="Standort"
            value={locationId}
            onChange={(e) => {
              setSelectedLocation(e.target.value as string);
              // The employee list is location-specific, so a carried-over id would filter everything away.
              setUserFilter("");
            }}
          >
            {locations.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                {location.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="absences-user-label">Mitarbeiter</InputLabel>
          <Select
            labelId="absences-user-label"
            label="Mitarbeiter"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value as string)}
          >
            <MenuItem value="">Alle</MenuItem>
            {userOptions.map(([id, name]) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="absences-type-label">Art</InputLabel>
          <Select
            labelId="absences-type-label"
            label="Art"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as string)}
          >
            <MenuItem value="">Alle</MenuItem>
            {typeOptions.map((type) => (
              <MenuItem key={type} value={type}>
                {getAbsenceTypeLabel(type)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          type="date"
          label="Von"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          type="date"
          label="Bis"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : visibleAbsences.length === 0 ? (
        <Typography sx={{ py: 2 }}>Keine Abwesenheiten für diese Auswahl.</Typography>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={9}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Mitarbeiter</TableCell>
                    <TableCell>Art</TableCell>
                    <TableCell>Von</TableCell>
                    <TableCell>Bis</TableCell>
                    <TableCell>Standort</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleAbsences.map((absence) => (
                    <TableRow
                      key={absence.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => openAbsence(absence)}
                    >
                      <TableCell>{absence.userName}</TableCell>
                      <TableCell>
                        <Chip size="small" label={getAbsenceTypeLabel(absence.loAType)} />
                      </TableCell>
                      <TableCell>{formatDay(absence.start)}</TableCell>
                      {/* The stored end is exclusive, the list shows the last absent day. */}
                      <TableCell>
                        {formatDay(moment(absence.end).subtract(1, "day").toISOString())}
                      </TableCell>
                      <TableCell>{(absence.locationName as string) || ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} md={3}>
            <FlatCard title="Zusammenfassung">
              <Grid container spacing={3} direction="column">
                <Grid item>
                  <Typography variant="body2" color="text.secondary">
                    Urlaub:
                  </Typography>
                  <Typography variant="h5" color="black" fontWeight="medium">
                    {summary.vacation} Tage
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2" color="text.secondary">
                    Resturlaub:
                  </Typography>
                  <Typography variant="h5" color="info.main" fontWeight="medium">
                    {summary.remainingVacation} Tage
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2" color="text.secondary">
                    Krank gesamt:
                  </Typography>
                  <Typography variant="h5" color="black" fontWeight="medium">
                    {summary.sickTotal} Tage
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    davon mit AU: {summary.sickWithNote} Tage
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    davon ohne AU: {summary.sickWithoutNote} Tage
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2" color="text.secondary">
                    Kindkrank gesamt:
                  </Typography>
                  <Typography variant="h5" color="black" fontWeight="medium">
                    {summary.childSickTotal} Tage
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    davon mit KAU: {summary.childSickWithNote} Tage
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    davon ohne KAU: {summary.childSickWithoutNote} Tage
                  </Typography>
                </Grid>
              </Grid>
            </FlatCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default LocationAbsences;
